import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, contentChild, input, Signal, TemplateRef} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {startWith, switchMap} from "rxjs";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {GruppeMedlemListeModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {map} from "rxjs/operators";
import {TranslateModule} from "@ngx-translate/core";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatTooltip} from "@angular/material/tooltip";
import {AvatarKomponent} from "../../avatar/avatar";
import {aktiveRollePerioderSomStreng} from "felles-ng2/modell/medlemModellHjelpere";
import {TipsKomponent} from "../../tips/tips";
import {NgTemplateOutlet} from "@angular/common";
import {oppdaterGuiVedEndringIFormArrayLengde} from "felles-ng2/infrastruktur/angular/reactiveForm";

const debug = initDebugger('VelgMedlemmerTabellKomponent');

export type VelgMedlemmerSkjemaType = ReturnType<typeof VelgMedlemmerTabellKomponent.opprettSkjema>;
export type VelgMedlemmerSkjemaVerdi = VelgMedlemmerSkjemaType['value'];

export type VelgMedlemmerSkjemaRadType = ReturnType<typeof VelgMedlemmerTabellKomponent.opprettSkjemarad>;

/**
 * Viser tabell med medlemmer og lar bruker velge multiple fra denne vhja avkryssningsbokser.
 */
@Component({
    selector: 'velg-medlemmer-tabell',
    templateUrl: 'velg-medlemmer-tabell.html',
    styleUrl: 'velg-medlemmer-tabell.scss',
    host: {'data-testid': 'velg-medlemmer-tabell'},
    imports: [
        TranslateModule,
        MatCheckbox,
        MatTooltip,
        ReactiveFormsModule,
        NgTemplateOutlet,
        AvatarKomponent,
        TipsKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgMedlemmerTabellKomponent {

    static opprettSkjema(medlemmer: GruppeMedlemListeModell[]) {
        const rader = medlemmer.map(medlem => VelgMedlemmerTabellKomponent.opprettSkjemarad(medlem, false));
        return new FormArray(rader)
    }

    static opprettSkjemarad(medlem: GruppeMedlemListeModell, valgt: boolean) {
        return new FormGroup({medlem: new FormControl(medlem), valgt: new FormControl(valgt)});
    }

    /** Valgfri referanse til ng-template som brukes for å legge til et ekstra th-element i tabellen */
    ekstraThInnhold = contentChild<TemplateRef<any>>('ekstraThInnhold')
    /** Valgfri referanse til ng-template som brukes for å legge til et ekstra td-element i hver rad i tabellen, aktuell rad blir gitt med via kontekst under navnet 'rad' */
    ekstraTdInnhold = contentChild<TemplateRef<any>>('ekstraTdInnhold')

    skjemakontroll = input.required<VelgMedlemmerSkjemaType>();

    antallValgt: Signal<number>;
    alleValgt = computed(() => this.antallValgt() == this.skjemakontroll().length);
    ingenValgt = computed(() => this.antallValgt() == 0);

    constructor(private cdRef: ChangeDetectorRef) {
        this.antallValgt = toSignal(toObservable(this.skjemakontroll).pipe(
          switchMap(skjema => skjema.valueChanges.pipe(startWith(skjema.value))),
          map(rad => rad.filter(rad => rad.valgt).length)
        ), {initialValue: 0});

        //FormArray.push (og øvrige metoder på FormArray) trigger ikke rendering - må derfor manuelt signalisere at det har skjedd endringer som skal tegnes ut
        oppdaterGuiVedEndringIFormArrayLengde(this.skjemakontroll, this.cdRef);
    }

    endreAlle() {
        const valgt = !this.alleValgt(); //dersom alle er valgt endrer vi til at ingen er valgt, ellers settes alle til valgt
        this.skjemakontroll().patchValue(this.skjemakontroll().value.map(rad => ({...rad, valgt})));
    }

    endreValgt(rad: VelgMedlemmerSkjemaRadType) {
        rad.controls.valgt.setValue(!rad.controls.valgt.value);
    }

    aktiveMedlemsroller(rad: VelgMedlemmerSkjemaRadType): string {
        return aktiveRollePerioderSomStreng(rad.value.medlem!);
    }
}
